// Deterministic DeckModel -> self-contained deck-system HTML compiler.
//
// `renderDeck(model)` returns a COMPLETE, self-contained HTML document string
// that reproduces the frozen boot scaffold from templates/slide-template.html
// (theme boot, fonts, deck-system.css, <deck-stage>, the four required overlay
// divs, the three deck-*.js controllers, and the DOMContentLoaded init) and
// emits one <section> per slide with every Block mapped to the canonical
// COMPONENTS.md classes.
//
// Asset paths use the ABSOLUTE /src/* convention (not the examples' relative
// ../../src/*). Generated decks render inside the SPA viewer's <iframe srcdoc>,
// where relative refs resolve against the parent origin; the Go server mounts
// the static library at /src, /components, /examples, and the Vite dev proxy
// forwards /src/deck-* and /src/theme-*. Absolute paths therefore resolve in
// BOTH local dev and production exactly like the static decks the server vends.
//
// XSS: every text field is HTML-escaped via escapeHtml(). Author-supplied class
// hooks (`slide.class`, `card.accent`) flow into class="" attributes and are
// therefore NOT escaped but SANITISED — slide.class to safe [A-Za-z0-9_-] tokens
// (sanitiseClassList) and card.accent to a fixed allowlist (CARD_ACCENTS) — so a
// hostile value cannot break out of the attribute.
//
// WAVE 3 — three new untrusted input shapes, each gated:
//   1. Image/avatar/QR URLs (media-*, figure, testimonial, cta) flow into a
//      src/href attribute. They are passed through sanitiseUrl(), which both
//      escapes for attribute context AND restricts the scheme to a safe set
//      (http, https, root-relative "/…", and image/* data URIs). Any other
//      scheme — javascript:, vbscript:, a non-image data:, etc. — yields an
//      empty string, so a hostile URL can never execute or break the attribute.
//   2. meta.accent flows into the <html> data-accent attribute. It is gated by
//      the DECK_ACCENTS allowlist (sanitiseAccent) — an unknown value is
//      dropped, exactly like card.accent.
//   3. feature-card / icon-list `icon` is an inline SVG string. This is an
//      AUDITED raw hatch (like raw-html) but NARROWED: sanitiseIconSvg() keeps
//      the value ONLY if it is a single, well-formed <svg>…</svg> element with
//      no <script>, no event-handler (on*=) attributes, and no javascript:
//      URLs; otherwise it is dropped to empty. This lets authors supply line
//      icons without opening a general HTML-injection hole. Documented on the
//      block interfaces in deck-model.ts.
//
// The sole fully-verbatim path remains the `raw-html` block, inserted as-is —
// the original audited escape hatch, documented in deck-model.ts.
//
// British spelling throughout. No emojis. Do NOT change the <deck-stage> API.

import type {
  Block,
  DeckModel,
  Slide,
  SlideVariant,
  CardItem,
  ImageRef,
  CoverTag,
  PricingCell,
  DetailRef,
  SlideBackground,
} from './deck-model';
import {
  DECK_ACCENTS,
  BACKGROUND_KINDS,
  BACKGROUND_TOKENS,
  BACKGROUND_SCRIMS,
  BACKGROUND_TONES,
  MOTION_PRESETS,
  TITLE_SIZES,
} from './deck-model';

/** Escape the five HTML-significant characters so user text cannot break out of
 *  an element or attribute context. Used on EVERY text field except raw-html. */
export function escapeHtml(value: string): string {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Like escapeHtml but turns literal newlines into <br>. Escapes FIRST (so the
 *  text is safe) THEN converts "\n" to "<br>", letting an author force a line
 *  break in a heading/title/lede by putting a literal newline in the string. */
export function escapeWithBreaks(value: string): string {
  return escapeHtml(value).replace(/\n/g, '<br>');
}

/** Map a slide variant to the <section> variant class. "content" carries no
 *  variant class (it is the default). Dividers conventionally pair with s-ink,
 *  but that is supplied by the author via `slide.class`, not forced here. */
function variantClass(variant: SlideVariant | undefined): string {
  switch (variant) {
    case 'cover':
      return 'cover';
    case 'divider':
      return 'divider';
    case 'statement':
      // Centred hero. Unlike divider/cover, the title block keeps its standard
      // .title-block markup (just centred by CSS) so a title renders normally.
      return 'statement';
    case 'closing':
    case 'content':
    case undefined:
    default:
      return '';
  }
}

/** Sanitise an author-supplied class string to safe CSS class tokens.
 *  Splits on whitespace and keeps only tokens matching [A-Za-z0-9_-]; anything
 *  else (quotes, angle brackets, spaces-in-token) is dropped. This is stronger
 *  than HTML-escaping for an attribute context: a real class name cannot contain
 *  those characters, so a hostile value like `s-ink"><script>…` is reduced to
 *  its safe tokens and can never break out of the class="" attribute. */
function sanitiseClassList(value: string): string {
  return value
    .split(/\s+/)
    .filter((tok) => /^[A-Za-z0-9_-]+$/.test(tok))
    .join(' ');
}

/** Allowed card label accents — anything outside this set is dropped so an
 *  attacker-supplied `card.accent` can never break out of the class attribute
 *  (the server validates only block.type, and the advanced JSON editor accepts
 *  arbitrary input, so the renderer is the authoritative gate). */
const CARD_ACCENTS = new Set(['ink', 'red', 'sage', 'clay']);

/** Allowed deck-wide accent presets (mirrors DECK_ACCENTS from the model). An
 *  unknown/hostile `meta.accent` is dropped so it can never reach the
 *  `data-accent` attribute. */
const DECK_ACCENT_SET = new Set<string>(DECK_ACCENTS);

/** Resolve `meta.accent` to a safe preset name, or '' if it is not allowed. */
function sanitiseAccent(value: string | undefined): string {
  return value && DECK_ACCENT_SET.has(value) ? value : '';
}

/** Sanitise a URL for use in a src/href attribute. Returns an attribute-safe,
 *  escaped string when the scheme is allowed, else '' (empty src). Allowed:
 *    · root-relative paths   ("/img/x.png")
 *    · protocol-relative      ("//host/x.png" — inherits page scheme)
 *    · http: / https:
 *    · image/* data URIs      ("data:image/png;base64,…")
 *  Everything else — javascript:, vbscript:, file:, non-image data:, etc. — is
 *  rejected. The check is done on a scheme-stripped, lower-cased, control-char-
 *  removed copy so obfuscation ("java\tscript:", "JaVaScRiPt:") cannot slip
 *  through; the ORIGINAL value is what gets escaped + emitted when allowed. */
function sanitiseUrl(value: string | undefined): string {
  if (!value) return '';
  const raw = String(value);
  // Strip ASCII control chars (incl. tab/newline used to break up "javascript:")
  // and surrounding whitespace, then lower-case for the scheme test only.
  // eslint-disable-next-line no-control-regex
  const probe = raw.replace(/[ -]/g, '').trim().toLowerCase();
  // Root-relative or protocol-relative — safe, no scheme to abuse.
  if (probe.startsWith('/')) return escapeHtml(raw.trim());
  // Explicit allowed schemes.
  if (probe.startsWith('http://') || probe.startsWith('https://')) {
    return escapeHtml(raw.trim());
  }
  // image/* data URIs only.
  if (probe.startsWith('data:image/')) return escapeHtml(raw.trim());
  // A scheme-less, dot-free token with no colon is treated as a relative path.
  if (!probe.includes(':')) return escapeHtml(raw.trim());
  // Anything with a colon and a non-allowed scheme is rejected.
  return '';
}

/** Build a complete `src="…"` (or other attribute) value, dropping it entirely
 *  when the URL is rejected so the element renders with no live src. */
function srcAttr(name: string, value: string | undefined): string {
  const safe = sanitiseUrl(value);
  return safe ? ` ${name}="${safe}"` : '';
}

// ── Slide-level background ────────────────────────────────────────────────
//
// The renderer is the authoritative gate (the advanced JSON editor accepts
// arbitrary input). Every background field is allow-listed against the model's
// canonical arrays; colour tokens map to Paper `var(--…)` — a raw colour value
// can never reach the inline style. The image path reuses srcAttr (the proven
// attribute-context sanitiser) on an <img>, NOT a CSS url(), so a hostile src
// can neither execute nor break out of the style attribute.

/** Allow-listed background colour token -> Paper CSS custom property. */
const BG_TOKEN_VAR: Record<string, string> = {
  paper: '--canvas',
  surface: '--surface',
  sunken: '--surface-sunken',
  ink: '--ink',
  navy: '--info',
  plum: '--danger',
  sage: '--success',
  clay: '--warning',
};
const BG_KIND_SET = new Set<string>(BACKGROUND_KINDS);
const BG_TOKEN_SET = new Set<string>(BACKGROUND_TOKENS);
const BG_SCRIM_SET = new Set<string>(BACKGROUND_SCRIMS);
const BG_TONE_SET = new Set<string>(BACKGROUND_TONES);
const MOTION_PRESET_SET = new Set<string>(MOTION_PRESETS);
const BG_POSITION_SET = new Set(['center', 'top', 'bottom']);
const TITLE_SIZE_SET = new Set<string>(TITLE_SIZES);

/** Resolve a colour token to a `var(--…)` reference, falling back to a safe
 *  default token when the value is missing or not allow-listed. */
function bgVar(token: string | undefined, fallback: string): string {
  const t = token && BG_TOKEN_SET.has(token) ? token : '';
  return `var(${t ? BG_TOKEN_VAR[t] : fallback})`;
}

/** Render the full-bleed background layer (+ optional scrim) for a slide. Emits
 *  absolutely-positioned elements so deck-stage.js hoists them OUT of the
 *  auto-fit wrapper (they fill the section edge-to-edge and never scale). An
 *  unknown `kind` yields '' (no background). */
function renderBackground(bg: SlideBackground): string {
  const kind = BG_KIND_SET.has(bg.kind) ? bg.kind : '';
  if (!kind) return '';
  const scrim = bg.scrim && BG_SCRIM_SET.has(bg.scrim) ? bg.scrim : '';
  const scrimEl =
    scrim && scrim !== 'none'
      ? `<div class="deck-bg-scrim deck-bg-scrim--${scrim}"></div>`
      : '';

  if (kind === 'motion') {
    const preset = bg.preset && MOTION_PRESET_SET.has(bg.preset) ? bg.preset : 'drift';
    return `<div class="deck-bg deck-bg--motion deck-bg--motion-${preset}"></div>${scrimEl}`;
  }
  if (kind === 'image') {
    const pos =
      bg.position && BG_POSITION_SET.has(bg.position) ? ` deck-bg-img--${bg.position}` : '';
    const img = `<img class="deck-bg-img${pos}"${srcAttr('src', bg.src)} alt="">`;
    return `<div class="deck-bg deck-bg--image">${img}</div>${scrimEl}`;
  }
  // color | gradient — inline style uses ONLY allow-listed var(--…) + a clamped
  // integer angle, so no untrusted value reaches the attribute.
  let style = '';
  if (kind === 'color') {
    style = `background:${bgVar(bg.token, '--canvas')};`;
  } else {
    const angle =
      typeof bg.angle === 'number' && Number.isFinite(bg.angle)
        ? Math.max(0, Math.min(360, Math.round(bg.angle)))
        : 135;
    style = `background:linear-gradient(${angle}deg, ${bgVar(bg.from, '--ink')}, ${bgVar(bg.to, '--info')});`;
  }
  return `<div class="deck-bg deck-bg--${kind}" style="${style}"></div>${scrimEl}`;
}

/** The section tone class for a background (flips content text colour for
 *  legibility over the background). '' when no/invalid tone. */
function backgroundToneClass(bg: SlideBackground | undefined): string {
  if (!bg) return '';
  return bg.tone && BG_TONE_SET.has(bg.tone) ? `bg-tone-${bg.tone}` : '';
}

/** AUDITED, NARROWED raw hatch for inline icon SVGs. Accepts the value ONLY if
 *  it is a single well-formed <svg>…</svg> element that contains no <script>,
 *  no inline event handlers (on*=), no javascript:/data: (non-image) URLs, and
 *  no <foreignObject> (which can host arbitrary HTML). Returns the original SVG
 *  verbatim when it passes, else '' so nothing hostile is ever emitted. This is
 *  deliberately conservative — it is an icon slot, not a general HTML sink. */
function sanitiseIconSvg(value: string | undefined): string {
  if (!value) return '';
  const svg = String(value).trim();
  // Must be a single <svg> root and nothing leading/trailing.
  if (!/^<svg[\s>]/i.test(svg) || !/<\/svg>\s*$/i.test(svg)) return '';
  const lower = svg.toLowerCase();
  // Reject any obviously dangerous construct.
  if (lower.includes('<script')) return '';
  if (lower.includes('<foreignobject')) return '';
  if (/\son[a-z-]+\s*=/.test(lower)) return ''; // onload=, onclick=, etc.
  if (lower.includes('javascript:')) return '';
  if (lower.includes('vbscript:')) return '';
  // Allow data:image/* but reject any other data: URI (e.g. data:text/html).
  if (/data:(?!image\/)/.test(lower)) return '';
  // Reject external entity / DOCTYPE shenanigans.
  if (lower.includes('<!doctype') || lower.includes('<!entity')) return '';
  return svg;
}

// ── Detail-modal registry (shared by feature-grid, panel, timeline) ─────────
//
// Click-to-expand cards/markers feed the deck's detail modal via
// DeckOverlays.registerDetails(map). The renderer mints a STABLE, namespaced key
// per detail (s${slideIndex}-${seq}), stores the detail payload RAW (NOT escaped
// — src/deck-overlays.js injects it via textContent, so the single, authoritative
// escape point is the DOM, not the JSON), and emits one <script> after the slides
// that registers the whole map. The <script>-context escaping on the JSON string
// (`<` → < etc.) prevents a `</script>` breakout, NOT element injection —
// that is the overlay's textContent job.

/** Module-level render state. Reset at the START of every renderDeck() call so a
 *  second compile never inherits the first deck's keys. */
let _detailRegistry: Record<string, unknown> = {};
/** 0-based index of the slide currently being rendered. Namespaces detail keys
 *  per slide so the counter can stay slide-local and keys stay globally unique. */
let _slideIndex = 0;
/** Per-slide monotonically-increasing sequence; reset at the start of each
 *  slide's block render so keys read s0-0, s0-1, s1-0, … */
let _detailSeq = 0;

/** Register a detail payload (when present) and return its stable namespaced
 *  key; returns '' when no detail is supplied. The stored body/meta are RAW
 *  (unescaped) — the overlay's textContent sink is the escape point. */
function registerDetail(detail: DetailRef | undefined): string {
  if (!detail) return '';
  const key = `s${_slideIndex}-${_detailSeq++}`;
  _detailRegistry[key] = {
    label: detail.label,
    title: detail.title,
    body: detail.body ?? [],
    meta: detail.meta ?? [],
  };
  return key;
}

/** JSON-encode an object for embedding inside a <script> tag. Neutralises the
 *  three sequences that could terminate the script element or be mis-parsed by
 *  the HTML/JS tokeniser: `<` (kills any `</script>` breakout), U+2028 and
 *  U+2029 (line separators that are valid JSON whitespace but ILLEGAL inside a
 *  JS string literal). JSON.parse reads all three back unchanged. */
function scriptSafeJson(obj: unknown): string {
  return JSON.stringify(obj)
    .replace(/</g, '\\u003c')
    .replace(/[\u2028]/g, '\\u2028')
    .replace(/[\u2029]/g, '\\u2029');
}

/** True when a cover slide carries a title block with a cover-rich pill, which
 *  promotes the section to `.cover.cover-rich` (see COMPONENTS.md). */
function isCoverRich(slide: Slide): boolean {
  if (slide.variant !== 'cover') return false;
  return slide.blocks.some(
    (b) => b.type === 'title' && !!b.coverTag && Array.isArray(b.coverTag.items),
  );
}

/** Build the space-separated class attribute value for a <section>, combining
 *  the variant class with any author-supplied extra classes (sanitised). */
function sectionClasses(slide: Slide): string {
  const parts: string[] = [];
  const vc = variantClass(slide.variant);
  if (vc) parts.push(vc);
  // A cover slide whose title block carries a coverTag becomes cover-rich.
  if (isCoverRich(slide)) parts.push('cover-rich');
  // A background with a tone flips the content text colour for legibility.
  const tone = backgroundToneClass(slide.background);
  if (tone) parts.push(tone);
  // A slide carrying any background gets a marker class (CSS scopes the content
  // stacking context above the hoisted background layer).
  if (slide.background && BG_KIND_SET.has(slide.background.kind)) parts.push('has-bg');
  if (slide.class) {
    const safe = sanitiseClassList(slide.class);
    if (safe) parts.push(safe);
  }
  return parts.join(' ').trim();
}

// ── Block renderers ──────────────────────────────────────────────────────────

function renderTitleBlock(b: Extract<Block, { type: 'title' }>, variant: SlideVariant | undefined): string {
  // On a divider the title block degrades to the .divider primitives
  // (.num + h1 + .sub) which the .divider section layout expects.
  if (variant === 'divider') {
    const num = b.eyebrow ? `<div class="num">${escapeHtml(b.eyebrow)}</div>` : '';
    const sub = b.lede ? `<p class="sub">${escapeWithBreaks(b.lede)}</p>` : '';
    return `${num}<h1>${escapeWithBreaks(b.title)}</h1>${sub}`;
  }
  // On a cover the eyebrow/lede degrade to the .cover primitives. An optional
  // coverTag adds the .cover-tag pill (and promotes the section to cover-rich
  // via sectionClasses).
  if (variant === 'cover') {
    const eyebrow = b.eyebrow ? `<p class="eyebrow red">${escapeHtml(b.eyebrow)}</p>` : '';
    const sub = b.lede ? `<p class="subtitle">${escapeWithBreaks(b.lede)}</p>` : '';
    const tag = b.coverTag ? renderCoverTag(b.coverTag) : '';
    return `${eyebrow}<h1>${escapeWithBreaks(b.title)}</h1>${sub}${tag}`;
  }
  // Standard content/closing title block. Optional size/align/overlay modifiers
  // are allow-listed into class hooks (default unset = current behaviour).
  const eyebrow = b.eyebrow ? `<div class="eyebrow">${escapeHtml(b.eyebrow)}</div>` : '';
  const lede = b.lede ? `<p class="lede">${escapeWithBreaks(b.lede)}</p>` : '';
  const mods: string[] = [];
  if (b.size && TITLE_SIZE_SET.has(b.size)) mods.push(`title--${b.size}`);
  if (b.align === 'center') mods.push('title--center');
  if (b.overlay) mods.push('title--overlay');
  const cls = mods.length ? `title-block ${mods.join(' ')}` : 'title-block';
  return `<div class="${cls}">${eyebrow}<h1 class="title">${escapeWithBreaks(b.title)}</h1>${lede}</div>`;
}

function renderHeadingBlock(b: Extract<Block, { type: 'heading' }>): string {
  switch (b.level) {
    case 1:
      return `<h1 class="title">${escapeWithBreaks(b.text)}</h1>`;
    case 2:
      return `<h2 class="subtitle">${escapeWithBreaks(b.text)}</h2>`;
    case 3:
    default:
      return `<h3 class="h3">${escapeWithBreaks(b.text)}</h3>`;
  }
}

function renderBulletsBlock(b: Extract<Block, { type: 'bullets' }>): string {
  const tag = b.ordered ? 'ol' : 'ul';
  const items = b.items
    .map((item, i) => {
      const n = String(i + 1).padStart(2, '0');
      return `<li><span class="n">${n}</span><span class="body">${escapeHtml(item)}</span></li>`;
    })
    .join('');
  return `<${tag} class="bullets">${items}</${tag}>`;
}

function renderCardItem(card: CardItem): string {
  // Allowlist the accent: an unknown/hostile value is dropped, never echoed
  // into the class attribute (defence against attribute breakout).
  const accent = card.accent && CARD_ACCENTS.has(card.accent) ? card.accent : '';
  const labelClass = accent ? `label ${accent}` : 'label';
  const label = card.label ? `<div class="${labelClass}">${escapeHtml(card.label)}</div>` : '';
  const desc = card.desc ? `<p class="desc">${escapeHtml(card.desc)}</p>` : '';
  return `<div class="card">${label}<h3>${escapeHtml(card.title)}</h3>${desc}</div>`;
}

function renderCardsBlock(b: Extract<Block, { type: 'cards' }>): string {
  const grid = b.columns === 3 ? 'three-col' : 'two-col';
  const cards = b.cards.map(renderCardItem).join('');
  return `<div class="${grid}">${cards}</div>`;
}

function renderStatRowBlock(b: Extract<Block, { type: 'stat-row' }>): string {
  const stats = b.stats
    .map((s) => {
      const nClass = s.accent === 'red' ? 'n red' : 'n';
      return `<div class="stat"><div class="${nClass}">${escapeHtml(s.n)}</div><div class="label">${escapeHtml(s.label)}</div></div>`;
    })
    .join('');
  return `<div class="stat-row">${stats}</div>`;
}

function renderQuoteBlock(b: Extract<Block, { type: 'quote' }>): string {
  const attribution = b.attribution
    ? `<p class="small">&mdash; ${escapeHtml(b.attribution)}</p>`
    : '';
  return `<p class="bigquote">${escapeHtml(b.text)}</p>${attribution}`;
}

function renderTableBlock(b: Extract<Block, { type: 'table' }>): string {
  const head = b.headers.map((h) => `<th>${escapeHtml(h)}</th>`).join('');
  const body = b.rows
    .map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`)
    .join('');
  return `<table class="tbl"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
}

function renderKvBlock(b: Extract<Block, { type: 'kv' }>): string {
  const items = b.items
    .map((it) => `<div class="item"><div class="key">${escapeHtml(it.k)}</div><div class="val">${escapeHtml(it.v)}</div></div>`)
    .join('');
  return `<div class="kv">${items}</div>`;
}

// ── WAVE 3 block renderers ───────────────────────────────────────────────

/** Render an <img> with a sanitised src + escaped alt. When the URL is
 *  rejected the element still renders (with no src) so layout is stable and the
 *  alt text remains visible. */
function renderImg(img: ImageRef, cls: string): string {
  const altAttr = ` alt="${escapeHtml(img.alt ?? '')}"`;
  return `<img class="${cls}"${srcAttr('src', img.src)}${altAttr}>`;
}

function renderMediaSplitBlock(
  b: Extract<Block, { type: 'media-split' }>,
): string {
  const cls = b.reverse ? 'media-split reverse' : 'media-split';
  // FIX 8: the image sits in a fixed-ratio crop wrapper (overflow:hidden +
  // --img-radius); the <img> fills it with object-fit:cover.
  const img = `<div class="media-crop">${renderImg(b.image, 'media-split-img')}</div>`;
  const inner = b.blocks.map((blk) => renderBlock(blk, undefined)).join('');
  const cap = b.caption
    ? `<div class="media-cap">${escapeHtml(b.caption)}</div>`
    : '';
  return `<div class="${cls}">${img}<div class="media-split-body">${inner}${cap}</div></div>`;
}

function renderMediaFullBlock(
  b: Extract<Block, { type: 'media-full' }>,
): string {
  const img = renderImg(b.image, 'media-full-img');
  const eyebrow = b.eyebrow
    ? `<p class="eyebrow">${escapeHtml(b.eyebrow)}</p>`
    : '';
  const title = b.title ? `<h1 class="title">${escapeWithBreaks(b.title)}</h1>` : '';
  return `<div class="media-full">${img}<div class="scrim"></div><div class="media-full-title">${eyebrow}${title}</div></div>`;
}

function renderMediaGridBlock(
  b: Extract<Block, { type: 'media-grid' }>,
): string {
  const cols = Math.min(4, Math.max(2, b.images.length || 2));
  // FIX 8: each tile's image sits in an aspect-ratio crop wrapper.
  const tiles = b.images
    .map((img) => {
      const crop = `<div class="media-crop media-tile-crop">${renderImg(img, 'media-tile')}</div>`;
      if (img.caption) {
        return `<figure class="media-tile-fig">${crop}<figcaption class="media-cap">${escapeHtml(img.caption)}</figcaption></figure>`;
      }
      return crop;
    })
    .join('');
  return `<div class="media-grid" style="--cols: ${cols}">${tiles}</div>`;
}

function renderFigureBlock(b: Extract<Block, { type: 'figure' }>): string {
  const altAttr = ` alt="${escapeHtml(b.image.alt ?? '')}"`;
  const img = `<img${srcAttr('src', b.image.src)}${altAttr}>`;
  const cap = b.caption
    ? `<figcaption class="figure-cap">${escapeHtml(b.caption)}</figcaption>`
    : '';
  // FIX 8: the image sits in an aspect-ratio crop wrapper (overflow:hidden +
  // object-fit:cover) so the figure crops cleanly to a fixed ratio.
  return `<figure class="figure"><div class="figure-crop">${img}</div>${cap}</figure>`;
}

function renderBignumBlock(b: Extract<Block, { type: 'bignum' }>): string {
  const cls = b.center ? 'bignum center' : 'bignum';
  const value = `<div class="bignum-value">${escapeHtml(b.value)}</div>`;
  const label = b.label
    ? `<div class="bignum-label">${escapeHtml(b.label)}</div>`
    : '';
  let delta = '';
  if (b.delta) {
    // Allowlist the direction so it can never break out of the class attribute.
    const dir = b.delta.dir === 'down' ? 'down' : 'up';
    delta = `<div class="bignum-delta ${dir}">${escapeHtml(b.delta.text)}</div>`;
  }
  return `<div class="${cls}">${value}${label}${delta}</div>`;
}

function renderTestimonialBlock(
  b: Extract<Block, { type: 'testimonial' }>,
): string {
  const quote = `<p class="testimonial-quote">${escapeHtml(b.quote)}</p>`;
  // Attribution name line: name, then "role, company" as the meta line.
  const name = b.name
    ? `<div class="by-name">${escapeHtml(b.name)}</div>`
    : '';
  const metaParts = [b.role, b.company].filter(
    (x): x is string => typeof x === 'string' && x.length > 0,
  );
  const meta = metaParts.length
    ? `<div class="by-meta">${metaParts.map(escapeHtml).join(', ')}</div>`
    : '';
  let by = '';
  if (name || meta || b.avatar) {
    const avatarCls = b.logo ? 'testimonial-avatar logo' : 'testimonial-avatar';
    const avatar = b.avatar
      ? `<img class="${avatarCls}"${srcAttr('src', b.avatar)} alt="">`
      : '';
    by = `<div class="testimonial-by">${avatar}<div class="by-text">${name}${meta}</div></div>`;
  }
  return `<div class="testimonial">${quote}${by}</div>`;
}

/** Per-series ramp class for chart bars/line: index 0 → default, 1 → s2 … 4 → s5
 *  (wraps after five). */
function seriesClass(index: number): string {
  const step = (index % 5) + 1; // 1..5
  return step === 1 ? '' : `s${step}`;
}

function renderChartBarsBlock(
  b: Extract<Block, { type: 'chart-bars' }>,
): string {
  const values = b.bars.map((bar) => (Number.isFinite(bar.value) ? bar.value : 0));
  const computedMax =
    typeof b.max === 'number' && b.max > 0 ? b.max : Math.max(1, ...values, 0);
  const cols = b.bars
    .map((bar, i) => {
      const v = values[i];
      // Clamp the height percentage to [0,100]; round to 1dp.
      const pct = Math.max(0, Math.min(100, (v / computedMax) * 100));
      const pctStr = `${pct.toFixed(1)}%`;
      const sc = seriesClass(i);
      const barCls = sc ? `chart-bar ${sc}` : 'chart-bar';
      const valueEl = `<span class="chart-bar-value">${escapeHtml(String(bar.value))}</span>`;
      const bar2 = `<div class="${barCls}" style="--v: ${pctStr}">${valueEl}</div>`;
      const labelEl = `<span class="chart-bar-label">${escapeHtml(bar.label)}</span>`;
      return `<div class="chart-col">${bar2}${labelEl}</div>`;
    })
    .join('');
  return `<div class="chart-bars">${cols}</div>`;
}

function renderDonutBlock(b: Extract<Block, { type: 'donut' }>): string {
  // Clamp the percentage to [0,100]; round to 1dp for the --p variable.
  const raw = Number.isFinite(b.value) ? b.value : 0;
  const pct = Math.max(0, Math.min(100, raw));
  const pStr = pct.toFixed(1);
  // SVG: 280x280 viewBox, centre 140,140, r=80 (matches the CSS dasharray 502.65).
  const svg =
    `<svg class="donut-svg" viewBox="0 0 280 280" aria-hidden="true">` +
    `<circle class="donut-track" cx="140" cy="140" r="80"></circle>` +
    `<circle class="donut-fill" cx="140" cy="140" r="80"></circle>` +
    `</svg>`;
  const value = `<div class="donut-value">${escapeHtml(String(b.value))}</div>`;
  const cap = b.label
    ? `<div class="donut-cap">${escapeHtml(b.label)}</div>`
    : '';
  const center = `<div class="donut-center">${value}${cap}</div>`;
  const donut = `<div class="donut" style="--p: ${pStr}">${svg}${center}</div>`;
  if (!b.segments || b.segments.length === 0) return donut;
  const keys = b.segments
    .map((seg, i) => {
      const sc = seriesClass(i);
      const keyCls = sc ? `donut-key ${sc}` : 'donut-key';
      return `<li class="${keyCls}">${escapeHtml(seg.label)}<span class="donut-key-value">${escapeHtml(String(seg.value))}</span></li>`;
    })
    .join('');
  const legend = `<ul class="donut-legend">${keys}</ul>`;
  return `${donut}${legend}`;
}

function renderChartLineBlock(
  b: Extract<Block, { type: 'chart-line' }>,
): string {
  // Plot geometry (viewBox units). Padding leaves room for the baseline.
  const W = 1000;
  const H = 360;
  const padX = 20;
  const padY = 20;
  const plotW = W - padX * 2;
  const plotH = H - padY * 2;
  // Global max across all series for a shared y-scale (min floor of 1).
  const allPoints = b.series.flatMap((s) =>
    s.points.filter((p) => Number.isFinite(p)),
  );
  const max = Math.max(1, ...allPoints, 0);
  const toXY = (pt: number, i: number, n: number): [number, number] => {
    const x = n <= 1 ? padX : padX + (plotW * i) / (n - 1);
    const v = Number.isFinite(pt) ? pt : 0;
    const y = padY + plotH - (Math.max(0, v) / max) * plotH;
    return [Math.round(x * 100) / 100, Math.round(y * 100) / 100];
  };
  const baseline = `<line class="cl-axis" x1="${padX}" y1="${padY + plotH}" x2="${W - padX}" y2="${padY + plotH}"></line>`;
  const groups = b.series
    .map((s, si) => {
      const n = s.points.length;
      const xy = s.points.map((pt, i) => toXY(pt, i, n));
      const linePts = xy.map(([x, y]) => `${x},${y}`).join(' ');
      const areaPts =
        n > 0
          ? `${padX},${padY + plotH} ${linePts} ${xy[n - 1][0]},${padY + plotH}`
          : '';
      const sc = seriesClass(si);
      const gCls = sc ? `cl-series ${sc}` : 'cl-series';
      const area = areaPts
        ? `<polygon class="cl-area" points="${areaPts}"></polygon>`
        : '';
      const line = `<polyline class="cl-line" points="${linePts}"></polyline>`;
      const dots = xy
        .map(([x, y]) => `<circle class="cl-dot" cx="${x}" cy="${y}" r="5"></circle>`)
        .join('');
      return `<g class="${gCls}">${area}${line}${dots}</g>`;
    })
    .join('');
  const svg = `<svg class="chart-line-svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" aria-hidden="true">${baseline}${groups}</svg>`;
  return `<div class="chart-line">${svg}</div>`;
}

function renderAgendaBlock(b: Extract<Block, { type: 'agenda' }>): string {
  const items = b.items
    .map((it) => {
      // active/done are mutually-styleable state classes — allowlisted booleans.
      const states: string[] = [];
      if (it.active) states.push('active');
      if (it.done) states.push('done');
      const cls = states.length
        ? `agenda-item ${states.join(' ')}`
        : 'agenda-item';
      const title = `<span class="agenda-title">${escapeHtml(it.text)}</span>`;
      const note = it.note
        ? `<span class="agenda-note">${escapeHtml(it.note)}</span>`
        : '';
      return `<li class="${cls}"><div>${title}${note}</div></li>`;
    })
    .join('');
  return `<ol class="agenda">${items}</ol>`;
}

/** Fixed allowlist of comparison cell markers. Anything else is rendered as
 *  escaped text in a plain centred cell. */
const COMPARE_MARKERS = new Set(['yes', 'no', 'partial']);

function renderCompareBlock(b: Extract<Block, { type: 'compare' }>): string {
  const head = b.headers
    .map((h) => `<th class="cell">${escapeHtml(h)}</th>`)
    .join('');
  const body = b.rows
    .map((row) => {
      const cells = row.cells
        .map((cell) => {
          if (COMPARE_MARKERS.has(cell)) {
            // Marker glyph is drawn by CSS ::before; cell stays text-empty.
            return `<td class="cell ${cell}"></td>`;
          }
          return `<td class="cell">${escapeHtml(cell)}</td>`;
        })
        .join('');
      return `<tr><td>${escapeHtml(row.label)}</td>${cells}</tr>`;
    })
    .join('');
  return `<table class="tbl compare"><thead><tr><th></th>${head}</tr></thead><tbody>${body}</tbody></table>`;
}

function renderProsConsBlock(
  b: Extract<Block, { type: 'proscons' }>,
): string {
  const prosHead = escapeHtml(b.prosHead ?? 'Pros');
  const consHead = escapeHtml(b.consHead ?? 'Cons');
  const pros = b.pros.map((p) => `<li>${escapeHtml(p)}</li>`).join('');
  const cons = b.cons.map((c) => `<li>${escapeHtml(c)}</li>`).join('');
  return (
    `<div class="proscons">` +
    `<div class="pros"><h3 class="proscons-head">${prosHead}</h3><ul>${pros}</ul></div>` +
    `<div class="cons"><h3 class="proscons-head">${consHead}</h3><ul>${cons}</ul></div>` +
    `</div>`
  );
}

function renderFeatureCardBlock(
  b: Extract<Block, { type: 'feature-card' }>,
): string {
  const iconSvg = sanitiseIconSvg(b.icon);
  const icon = iconSvg ? `<div class="feature-icon">${iconSvg}</div>` : '';
  const title = `<h3>${escapeHtml(b.title)}</h3>`;
  const desc = b.desc ? `<p class="desc">${escapeHtml(b.desc)}</p>` : '';
  return `<div class="feature-card">${icon}${title}${desc}</div>`;
}

function renderIconListBlock(
  b: Extract<Block, { type: 'icon-list' }>,
): string {
  const items = b.items
    .map((it) => {
      const iconSvg = sanitiseIconSvg(it.icon);
      const icon = `<span class="icon">${iconSvg}</span>`;
      const sub = it.sub
        ? `<span class="icon-sub">${escapeHtml(it.sub)}</span>`
        : '';
      const text = `<div class="icon-text">${escapeHtml(it.text)}${sub}</div>`;
      return `<li class="icon-item">${icon}${text}</li>`;
    })
    .join('');
  return `<ul class="icon-list">${items}</ul>`;
}

function renderCtaBlock(b: Extract<Block, { type: 'cta' }>): string {
  const headline = `<h1 class="cta-headline">${escapeHtml(b.title)}</h1>`;
  const sub = b.sub ? `<p class="cta-sub">${escapeHtml(b.sub)}</p>` : '';
  let contact = '';
  if (b.contact && b.contact.length) {
    const rows = b.contact
      .map(
        (c) =>
          `<div class="key">${escapeHtml(c.k)}</div><div class="val">${escapeHtml(c.v)}</div>`,
      )
      .join('');
    contact = `<div class="cta-contact">${rows}</div>`;
  }
  const qr = b.qr
    ? `<div class="cta-qr"><img${srcAttr('src', b.qr)} alt=""></div>`
    : '';
  const foot = contact || qr ? `<div class="cta-foot">${contact}${qr}</div>` : '';
  return `<div class="cta">${headline}${sub}${foot}</div>`;
}

// ── DRIFT-CLOSING + TIER-C block renderers ────────────────────────────────
//
// Each emits the EXACT markup/classes documented in components/COMPONENTS.md
// (drift) or specified by the TIER-C contract (new CSS). Every text field is
// escaped via escapeHtml; every tint/accent/category/app-key input is dropped
// to a fixed allowlist before it reaches a class attribute, exactly like the
// existing CARD_ACCENTS / sanitiseAccent gates.

/** The cover-rich `.cover-tag` pill: a monospace key cap then dot-separated
 *  text spans. Used only on a cover-variant title block. */
function renderCoverTag(tag: CoverTag): string {
  const key = tag.key
    ? `<span class="key">${escapeHtml(tag.key)}</span>`
    : '';
  const items = (tag.items ?? []).map((it) => escapeHtml(it));
  // Interleave a .dot separator between spans (and after the key when present).
  const spans: string[] = [];
  if (key) spans.push(key);
  items.forEach((it) => {
    if (spans.length) spans.push('<span class="dot"></span>');
    spans.push(`<span>${it}</span>`);
  });
  return `<div class="cover-tag" aria-hidden="true">${spans.join('')}</div>`;
}

function renderAppsHubBlock(b: Extract<Block, { type: 'apps-hub' }>): string {
  // Clamp the column count to a sane 1..6, defaulting to 3.
  const cols =
    typeof b.cols === 'number' && b.cols >= 1 && b.cols <= 6
      ? Math.floor(b.cols)
      : 3;
  const tiles = b.tiles
    .map((t) => {
      const tileCls = t.experimental ? 'apps-hub-tile experimental' : 'apps-hub-tile';
      const eyebrowCls = t.experimental ? 'eyebrow' : 'eyebrow red';
      const eyebrow = t.eyebrow
        ? `<p class="${eyebrowCls}">${escapeHtml(t.eyebrow)}</p>`
        : '';
      const name = `<h3>${escapeHtml(t.name)}</h3>`;
      const url = t.url
        ? `<p class="tile-url">${escapeHtml(t.url)}</p>`
        : '';
      const note = t.note ? `<p class="desc">${escapeHtml(t.note)}</p>` : '';
      return `<div class="${tileCls}">${eyebrow}${name}${url}${note}</div>`;
    })
    .join('');
  const grid = `<div class="apps-hub" style="--cols: ${cols}">${tiles}</div>`;
  // FIX 5: optional centred Cmd-K switcher card ABOVE the tile grid, matching
  // the original "Six apps, one switcher" slide. The glyph defaults to the
  // command key when no key is supplied.
  let switcher = '';
  if (b.switcher) {
    const glyph = `<div class="ahs-glyph" aria-hidden="true">${escapeHtml(b.switcher.key || '⌘')}</div>`;
    const label = `<div class="ahs-label">${escapeHtml(b.switcher.label)}</div>`;
    const hint = b.switcher.hint
      ? `<div class="ahs-hint">${escapeHtml(b.switcher.hint)}</div>`
      : '';
    switcher = `<div class="apps-hub-switcher">${glyph}${label}${hint}</div>`;
  }
  return `${switcher}${grid}`;
}

function renderTierCapBlock(b: Extract<Block, { type: 'tier-cap' }>): string {
  // A single-tier strip collapses the grid to one column.
  const single = b.caps.some((c) => c.single);
  const gridCls = single ? 'tier-grid single' : 'tier-grid';
  const caps = b.caps
    .map((c) => {
      // featured / intel are mutually-styleable allowlisted state classes.
      const mods: string[] = [];
      if (c.featured) mods.push('featured');
      if (c.intel) mods.push('intel');
      const cls = mods.length ? `tier-cap ${mods.join(' ')}` : 'tier-cap';
      return `<div class="${cls}"><h3>${escapeHtml(c.name)}</h3><p>${escapeHtml(c.what)}</p></div>`;
    })
    .join('');
  return `<div class="${gridCls}">${caps}</div>`;
}

function renderRegionGridBlock(
  b: Extract<Block, { type: 'region-grid' }>,
): string {
  const cards = b.regions
    .map((r) => {
      const items = r.items
        .map((it) => {
          const feedback = `<p class="ri-feedback">${escapeHtml(it.feedback)}</p>`;
          const response = it.response
            ? `<p class="ri-response">${escapeHtml(it.response)}</p>`
            : '';
          return `<div class="region-item">${feedback}${response}</div>`;
        })
        .join('');
      return (
        `<div class="region-card">` +
        `<p class="eyebrow red">${escapeHtml(r.label)}</p>` +
        `<div class="region-items">${items}</div>` +
        `</div>`
      );
    })
    .join('');
  return `<div class="region-grid">${cards}</div>`;
}

/** Allowed signal-flow source dot tints — anything else is dropped so a hostile
 *  value can never break out of the inline style attribute. */
const SIGNAL_ICON_TINTS = new Set([
  'info',
  'warning',
  'success',
  'accent-cool',
  'danger',
]);

function renderSignalFlowBlock(
  b: Extract<Block, { type: 'signal-flow' }>,
): string {
  const sources = b.sources
    .map((s) => {
      const tint = s.icon && SIGNAL_ICON_TINTS.has(s.icon) ? s.icon : 'info';
      const icon = `<span class="src-icon" style="background:var(--${tint});"></span>`;
      const sub = s.sub
        ? ` <span class="src-sub">${escapeHtml(s.sub)}</span>`
        : '';
      return `<div class="sig-source">${icon}${escapeHtml(s.name)}${sub}</div>`;
    })
    .join('');
  const engEyebrow = b.engine.sub
    ? `<div class="eng-eyebrow">${escapeHtml(b.engine.sub)}</div>`
    : '';
  const engine =
    `<div class="sig-engine">${engEyebrow}` +
    `<h3>${escapeHtml(b.engine.name)}</h3></div>`;
  const surfaces = b.surfaces
    .map((s) => {
      const eg = s.eg
        ? `<div class="surf-eg">${escapeHtml(s.eg)}</div>`
        : '';
      return `<div class="sig-surface"><div class="surf-name">${escapeHtml(s.name)}</div>${eg}</div>`;
    })
    .join('');
  return (
    `<div class="signal-flow">` +
    `<div class="sig-col"><div class="sig-col-title">Sources</div>${sources}</div>` +
    `<div class="sig-flow-arrow">&rarr;</div>` +
    engine +
    `<div class="sig-flow-arrow">&rarr;</div>` +
    `<div class="sig-col"><div class="sig-col-title">Surfaces</div>${surfaces}</div>` +
    `</div>`
  );
}

/** Allowed floorplan pin/app keys — drives the .emp/.man/.ins/.vis colour
 *  modifier. Unknown keys are dropped (no modifier) so they can never break
 *  out of the class attribute. */
const FLOORPLAN_APPS = new Set(['emp', 'man', 'ins', 'vis']);

/** Round an author-supplied number for safe injection into an SVG coordinate
 *  attribute. Non-finite values collapse to 0 so the diagram never throws. */
function svgNum(value: number): number {
  return Number.isFinite(value) ? Math.round(value * 100) / 100 : 0;
}

function renderFloorplanBlock(
  b: Extract<Block, { type: 'floorplan' }>,
): string {
  const rooms = b.rooms
    .map((r) => {
      const rect = `<rect class="fp-room" x="${svgNum(r.x)}" y="${svgNum(r.y)}" width="${svgNum(r.w)}" height="${svgNum(r.h)}" rx="3"/>`;
      const label = r.label
        ? `<text class="fp-label" x="${svgNum(r.x + r.w / 2)}" y="${svgNum(r.y + r.h / 2)}" text-anchor="middle" dominant-baseline="middle">${escapeHtml(r.label)}</text>`
        : '';
      return `${rect}${label}`;
    })
    .join('');
  // Pins render as coloured dots ON the plan; the app labels + descriptions
  // live in an HTML legend BELOW the plan (.fp-legend). Free-floating SVG
  // callouts collided whenever two pins sat close together (and the long lead
  // descriptions are too wide to lay out in-canvas), so the legend is the
  // robust layout — it never overlaps and wraps long text cleanly.
  const dots = b.pins
    .map((p) => {
      // Allowlist the app key so it can never break out of the class attribute.
      const app = FLOORPLAN_APPS.has(p.app) ? p.app : '';
      const pinCls = app ? `fp-pin ${app}` : 'fp-pin';
      return `<circle class="${pinCls}" cx="${svgNum(p.x)}" cy="${svgNum(p.y)}" r="9"/>`;
    })
    .join('');
  // Frame the viewBox tightly around the room rectangles (x 100-1000, y
  // 120-290) now that no callouts sit above them.
  const svg =
    `<svg class="floorplan-svg" viewBox="0 100 1100 205" preserveAspectRatio="xMidYMid meet">` +
    `${rooms}${dots}</svg>`;
  const legend = b.pins.length
    ? `<div class="fp-legend">` +
      b.pins
        .map((p) => {
          const app = FLOORPLAN_APPS.has(p.app) ? p.app : '';
          const dotCls = app ? `fp-legend-dot ${app}` : 'fp-legend-dot';
          const appCls = app ? `fp-legend-app ${app}` : 'fp-legend-app';
          const lead = p.lead
            ? `<span class="fp-legend-lead">${escapeHtml(p.lead)}</span>`
            : '';
          return (
            `<div class="fp-legend-item">` +
            `<span class="${dotCls}" aria-hidden="true"></span>` +
            `<div class="fp-legend-text"><span class="${appCls}">${escapeHtml(p.label)}</span>${lead}</div>` +
            `</div>`
          );
        })
        .join('') +
      `</div>`
    : '';
  let stats = '';
  if (b.kpis && b.kpis.length) {
    const cells = b.kpis
      .map(
        (k) =>
          `<div class="fp-stat"><div class="num">${escapeHtml(k.n)}</div><div class="lbl">${escapeHtml(k.label)}</div></div>`,
      )
      .join('');
    const twoCls = b.kpis.length === 2 ? ' fp-stats-two' : '';
    stats = `<div class="fp-stats${twoCls}">${cells}</div>`;
  }
  return `<div class="floorplan-card">${svg}${legend}${stats}</div>`;
}

/** Allowed layer-band colour tints. */
const LAYER_BAND_TINTS = new Set(['intel', 'ops', 'spatial']);

function renderLayerBandBlock(
  b: Extract<Block, { type: 'layer-band' }>,
): string {
  return b.bands
    .map((band, i) => {
      const tint = band.tint && LAYER_BAND_TINTS.has(band.tint) ? band.tint : '';
      const cls = tint ? `layer-band ${tint}` : 'layer-band';
      const arrow =
        i > 0
          ? `<div class="layer-down-arrow" aria-hidden="true">&uarr;</div>`
          : '';
      // FIX 4: emit the original two-column head/desc structure — a .band-head
      // (colour-coded .band-name) on the left, the .band-desc on the right.
      // The model carries no per-band eyebrow, so the empty .eyebrow that used
      // to render (and showed as a blank line) is dropped entirely.
      const bandHtml =
        `<div class="${cls}">` +
        `<div class="band-head"><h3 class="band-name">${escapeHtml(band.name)}</h3></div>` +
        `<p class="band-desc">${escapeHtml(band.desc)}</p>` +
        `</div>`;
      return `${arrow}${bandHtml}`;
    })
    .join('');
}

/** Allowed ai-card status values — maps to the data-status attribute. */
const AI_CARD_STATUS = new Set(['live', 'planned']);

function renderAiStackBlock(b: Extract<Block, { type: 'ai-stack' }>): string {
  // DE-NESTED (FIX 2): the .ai-tier is no longer a bordered card wrapping the
  // grid — it is a chrome-free group. An optional tier `name` renders as a small
  // eyebrow ABOVE that tier's flat .ai-card-grid (never an outer card title), so
  // the cards sit directly on the slide and the text can breathe at full size.
  const tiers = b.tiers
    .map((tier) => {
      const cols = Math.max(1, Math.min(6, tier.cards.length || 1));
      const name = tier.name
        ? `<div class="ai-tier-name">${escapeHtml(tier.name)}</div>`
        : '';
      const cards = tier.cards
        .map((c) => {
          // status: live -> "on" data-status; planned -> "planned"; default on.
          let statusAttr = '';
          if (c.status && AI_CARD_STATUS.has(c.status)) {
            const ds = c.status === 'planned' ? 'planned' : 'on';
            statusAttr = ` data-status="${ds}"`;
          }
          const cardCls = c.featured ? 'ai-card featured' : 'ai-card';
          const dot = `<span class="dot" aria-hidden="true"></span>`;
          const eyebrow = c.eyebrow
            ? `<div class="eyebrow red">${escapeHtml(c.eyebrow)}</div>`
            : '';
          const title = `<h3>${escapeHtml(c.title)}</h3>`;
          const desc = c.desc
            ? `<p class="desc">${escapeHtml(c.desc)}</p>`
            : '';
          const loc = c.loc ? `<p class="label">${escapeHtml(c.loc)}</p>` : '';
          return `<div class="${cardCls}"${statusAttr}>${dot}${eyebrow}${title}${desc}${loc}</div>`;
        })
        .join('');
      const grid = `<div class="ai-card-grid" style="--cols: ${cols}">${cards}</div>`;
      return `<div class="ai-tier">${name}${grid}</div>`;
    })
    .join('');
  return `<div class="ai-stack">${tiers}</div>`;
}

function renderEngineGridBlock(
  b: Extract<Block, { type: 'engine-grid' }>,
): string {
  const cards = b.engines
    .map((e) => {
      const eyebrow = e.eyebrow
        ? `<div class="eyebrow red">${escapeHtml(e.eyebrow)}</div>`
        : '';
      const title = `<h3>${escapeHtml(e.title)}</h3>`;
      const code = e.code
        ? `<p class="code-chip">${escapeHtml(e.code)}</p>`
        : '';
      const desc = e.desc ? `<p class="desc">${escapeHtml(e.desc)}</p>` : '';
      const quote = e.quote
        ? `<p class="quote">${escapeHtml(e.quote)}</p>`
        : '';
      return `<div class="engine-card">${eyebrow}${title}${code}${desc}${quote}</div>`;
    })
    .join('');
  return `<div class="engine-grid">${cards}</div>`;
}

function renderCycleBlock(b: Extract<Block, { type: 'cycle' }>): string {
  const points = b.points
    .map((p) => {
      const label = `<div class="cyc-label">${escapeHtml(p.label)}</div>`;
      const text = p.desc
        ? `<div class="cyc-text">${escapeHtml(p.desc)}</div>`
        : '';
      return `<div class="cycle-point">${label}${text}</div>`;
    })
    .join('');
  // Only the data-driven cadence caption grid is emitted. The previous
  // decorative empty cycle-wrap/cycle-svg rendered as a blank card, so it has
  // been removed entirely — just the three cadence point cards remain.
  return `<div class="cycle-points">${points}</div>`;
}

function renderRoadmapBlock(b: Extract<Block, { type: 'roadmap' }>): string {
  // Compact year-axis: viewBox 0 0 1700 250, axis at y=200 between x=210 and
  // x=1490. The generous side margins keep the centred end labels inside the
  // box (at x=60/1640 they used to clip off both edges), and the short height
  // removes the empty band that previously sat below the axis.
  const axisY = 200;
  const x0 = 210;
  const x1 = 1490;
  const n = b.milestones.length;
  const axis = `<line class="rm-axis" x1="${x0}" y1="${axisY}" x2="${x1}" y2="${axisY}"/>`;
  const groups = b.milestones
    .map((m, i) => {
      // Spread milestones evenly across the axis span.
      const cx =
        n <= 1 ? (x0 + x1) / 2 : svgNum(x0 + ((x1 - x0) * i) / (n - 1));
      const dotCls = m.future ? 'rm-dot future' : 'rm-dot';
      const dot = `<circle class="${dotCls}" cx="${cx}" cy="${axisY}" r="10"/>`;
      const lead = `<line class="rm-lead" x1="${cx}" y1="${axisY}" x2="${cx}" y2="86"/>`;
      const title = `<text class="rm-title" x="${cx}" y="66" text-anchor="middle">${escapeHtml(m.label)}</text>`;
      const sub = `<text class="rm-sub" x="${cx}" y="40" text-anchor="middle">${escapeHtml(m.when)}</text>`;
      return `<g>${dot}${lead}${title}${sub}</g>`;
    })
    .join('');
  const svg = `<svg class="roadmap-svg" viewBox="0 0 1700 250">${axis}${groups}</svg>`;
  return `<div class="roadmap">${svg}</div>`;
}

/** Allowed bar-h fill tints. */
const BAR_H_TONES = new Set(['warn', 'ok', 'muted']);

function renderBarHBlock(b: Extract<Block, { type: 'bar-h' }>): string {
  const values = b.bars.map((bar) => (Number.isFinite(bar.value) ? bar.value : 0));
  const computedMax =
    typeof b.max === 'number' && b.max > 0 ? b.max : Math.max(1, ...values, 0);
  return b.bars
    .map((bar, i) => {
      const v = values[i];
      // Width encodes the value against a 400-unit track; clamp to [0,400].
      const w = Math.max(0, Math.min(400, (v / computedMax) * 400));
      const wStr = w.toFixed(1);
      const tone = bar.tone && BAR_H_TONES.has(bar.tone) ? bar.tone : '';
      const barCls = tone ? `bar-h ${tone}` : 'bar-h';
      const row =
        `<div class="bar-h-row">` +
        `<span class="bar-h-label">${escapeHtml(bar.label)}</span>` +
        `<span class="bar-h-value">${escapeHtml(String(bar.value))}</span>` +
        `</div>`;
      const svg =
        `<div class="${barCls}">` +
        `<svg class="bar-h-svg" viewBox="0 0 400 14" preserveAspectRatio="none">` +
        `<rect class="bar-h-track" x="0" y="0" width="400" height="14" rx="2"/>` +
        `<rect class="bar-h-fill" x="0" y="0" width="${wStr}" height="14" rx="2"/>` +
        `</svg></div>`;
      return `${row}${svg}`;
    })
    .join('');
}

function renderPricingCell(cell: string | PricingCell): string {
  if (typeof cell === 'string') {
    return `<td class="price">${escapeHtml(cell)}</td>`;
  }
  const cls = cell.intel ? 'price intel' : 'price';
  return `<td class="${cls}">${escapeHtml(cell.value)}</td>`;
}

function renderPricingTblBlock(
  b: Extract<Block, { type: 'pricing-tbl' }>,
): string {
  const colCount = b.columns.length;
  const head = b.columns
    .map((c, i) => {
      // First column is the plain SKU header; the rest are tier-name headers.
      if (i === 0) return `<th>${escapeHtml(c.label)}</th>`;
      const cls = c.intel ? 'tier-name tier-intel' : 'tier-name';
      return `<th class="${cls}">${escapeHtml(c.label)}</th>`;
    })
    .join('');
  const body = b.rows
    .map((row) => {
      if (row.single) {
        const text = row.cells
          .map((c) => (typeof c === 'string' ? c : c.value))
          .map(escapeHtml)
          .join(' &middot; ');
        return `<tr class="single-tier"><td colspan="${colCount}">${text}</td></tr>`;
      }
      const cells = row.cells
        .map((c, i) => {
          // First cell is the SKU name; the remainder are price cells.
          if (i === 0) {
            const v = typeof c === 'string' ? c : c.value;
            return `<td class="sku">${escapeHtml(v)}</td>`;
          }
          return renderPricingCell(c);
        })
        .join('');
      return `<tr>${cells}</tr>`;
    })
    .join('');
  return `<table class="pricing-tbl"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
}

// ── TIER-C renderers (new CSS by the CSS agent) ───────────────────────────

/** Allowed callout-banner accents. Default is red (drop unknown). */
const CALLOUT_ACCENTS = new Set(['red', 'sage', 'clay', 'ink', 'info']);

function renderCalloutBannerBlock(
  b: Extract<Block, { type: 'callout-banner' }>,
): string {
  // SOFTENED (FIX 1): the calm tinted callout with a left accent bar. The
  // accent token now flows in as a bare class (red|info|sage|clay|ink) and the
  // eyebrow/text become the .cb-eyebrow / .cb-text inline row matching the
  // original hand-crafted deck. Default accent is red; unknowns are dropped.
  const accent = b.accent && CALLOUT_ACCENTS.has(b.accent) ? b.accent : 'red';
  const eyebrow = b.eyebrow
    ? `<div class="cb-eyebrow">${escapeHtml(b.eyebrow)}</div>`
    : '';
  const text = `<div class="cb-text">${escapeHtml(b.text)}</div>`;
  return `<div class="callout-banner ${accent}">${eyebrow}${text}</div>`;
}

function renderEquationBlock(b: Extract<Block, { type: 'equation' }>): string {
  const terms = b.terms.map((t) => escapeHtml(t));
  // Interleave a multiplication operator between terms.
  const parts: string[] = [];
  terms.forEach((t, i) => {
    if (i > 0) parts.push('<span class="eq-op">&times;</span>');
    parts.push(`<span class="eq-term">${t}</span>`);
  });
  const result = `<span class="eq-result">${escapeHtml(b.result)}</span>`;
  return `<div class="equation">${parts.join('')}${result}</div>`;
}

/** Allowed chip categories — drives the chip colour from a small token palette.
 *  Unknown categories are dropped so they can never break out of the data
 *  attribute or the class. */
const CHIP_CATEGORIES = new Set([
  'ink',
  'success',
  'warning',
  'danger',
  'info',
]);

function renderChipWallChip(label: string, category: string | undefined, sub?: string): string {
  const cat = category && CHIP_CATEGORIES.has(category) ? category : '';
  const catAttr = cat ? ` data-category="${cat}"` : '';
  const subEl = sub ? `<span class="chip-sub">${escapeHtml(sub)}</span>` : '';
  return `<span class="chip"${catAttr}>${escapeHtml(label)}${subEl}</span>`;
}

function renderChipWallBlock(b: Extract<Block, { type: 'chip-wall' }>): string {
  const head = b.title
    ? `<div class="chip-wall-head">${escapeHtml(b.title)}</div>`
    : '';
  const chips = b.chips
    .map((c) => renderChipWallChip(c.label, c.category, c.sub))
    .join('');
  let legend = '';
  if (b.legend && b.legend.length) {
    const items = b.legend
      .map((l) => {
        const cat = CHIP_CATEGORIES.has(l.category) ? l.category : '';
        const catAttr = cat ? ` data-category="${cat}"` : '';
        return `<span class="chip-legend-item"${catAttr}>${escapeHtml(l.label)}</span>`;
      })
      .join('');
    legend = `<div class="chip-wall-legend">${items}</div>`;
  }
  return `<div class="chip-wall">${head}<div class="chip-wall-chips">${chips}</div>${legend}</div>`;
}

function renderCommandHubBlock(
  b: Extract<Block, { type: 'command-hub' }>,
): string {
  const key = `<div class="command-hub-key" aria-hidden="true">&#8984;K</div>`;
  const label = `<div class="command-hub-label">${escapeHtml(b.label)}</div>`;
  const hint = b.hint
    ? `<div class="command-hub-hint">${escapeHtml(b.hint)}</div>`
    : '';
  let items = '';
  if (b.items && b.items.length) {
    const ring = b.items
      .map((it) => `<span class="command-hub-item">${escapeHtml(it)}</span>`)
      .join('');
    items = `<div class="command-hub-items">${ring}</div>`;
  }
  return `<div class="command-hub">${key}${label}${hint}${items}</div>`;
}

function renderBeforeAfterCard(
  col: { title: string; items: string[] },
  after: boolean,
): string {
  const cls = after ? 'ba-card after' : 'ba-card';
  const eyebrow = `<div class="ba-eyebrow">${escapeHtml(col.title)}</div>`;
  const chips = col.items
    .map((it) => `<span class="ba-chip">${escapeHtml(it)}</span>`)
    .join('');
  return `<div class="${cls}">${eyebrow}<div class="ba-chips">${chips}</div></div>`;
}

function renderBeforeAfterBlock(
  b: Extract<Block, { type: 'before-after' }>,
): string {
  // Matches the original .frag-grid (FIX 3): three-column grid with a centre
  // arrow column; the AFTER card is danger-tinted with a danger border.
  const before = renderBeforeAfterCard(b.before, false);
  const after = renderBeforeAfterCard(b.after, true);
  const arrow = `<div class="ba-arrow" aria-hidden="true">&rarr;</div>`;
  return `<div class="before-after">${before}${arrow}${after}</div>`;
}

// ── V5 renderers (flexible layouts + restored composites) ─────────────────

/** Labelled value/total progress bars — the deck's signature bar. Each row is a
 *  label + value caption over an SVG track/fill. `total` divides `value`;
 *  absent `total` treats `value` as a 0-100 percentage. `accent` switches the
 *  fill + label to the danger token. */
function renderProgressBlock(b: Extract<Block, { type: 'progress' }>): string {
  const rows = b.items
    .map((item) => {
      const value = Number.isFinite(item.value) ? item.value : 0;
      const total =
        typeof item.total === 'number' && Number.isFinite(item.total)
          ? item.total
          : undefined;
      // Fraction filled: value/total, or value/100 when no total.
      const rawPct = total ? (total !== 0 ? value / total : 0) : value / 100;
      const pct = Math.max(0, Math.min(1, rawPct));
      const fillW = Math.round(pct * 400);
      const display =
        item.display ?? (total ? `${item.value} / ${item.total}` : `${item.value}%`);
      const rowCls = item.accent ? 'prog-row accent' : 'prog-row';
      return (
        `<div class="${rowCls}">` +
        `<div class="prog-head">` +
        `<span class="prog-label">${escapeHtml(item.label)}</span>` +
        `<span class="prog-value">${escapeHtml(display)}</span>` +
        `</div>` +
        `<svg class="prog-track" viewBox="0 0 400 14" preserveAspectRatio="none">` +
        `<rect class="prog-bg" x="0" y="0" width="400" height="14" rx="2"/>` +
        `<rect class="prog-fill" x="0" y="0" width="${fillW}" height="14" rx="2"/>` +
        `</svg>` +
        `</div>`
      );
    })
    .join('');
  return `<div class="progress">${rows}</div>`;
}

/** A year-axis timeline with markers above/below the axis + an optional
 *  "ongoing" rail. A marker carrying `detail` becomes a clickable .tl-dot
 *  (data-tl="KEY") that opens the detail modal. SVG fills come from CSS tokens
 *  (no inline hex) so the diagram adapts in dark mode. */
function renderTimelineBlock(b: Extract<Block, { type: 'timeline' }>): string {
  const axisY = 280;
  const x0 = 60;
  const x1 = 1640;
  const span = x1 - x0;

  const parts: string[] = [];
  parts.push(`<line class="tl-axis" x1="${x0}" y1="${axisY}" x2="${x1}" y2="${axisY}"/>`);

  // Axis ticks + year labels.
  if (b.ticks && b.ticks.length) {
    const tn = b.ticks.length;
    b.ticks.forEach((tick, i) => {
      const tx = tn <= 1 ? x0 : Math.round(x0 + (span * i) / (tn - 1));
      parts.push(`<line class="tl-tick" x1="${tx}" y1="276" x2="${tx}" y2="290"/>`);
      parts.push(
        `<text class="tl-year" x="${tx}" y="320" text-anchor="middle">${escapeHtml(tick)}</text>`,
      );
    });
  }

  // Markers.
  const mn = b.markers.length;
  b.markers.forEach((m, i) => {
    const cx =
      typeof m.at === 'number' && Number.isFinite(m.at)
        ? Math.round(x0 + span * m.at)
        : Math.round(x0 + (span * i) / Math.max(1, mn - 1));
    const side = m.side ?? (i % 2 === 0 ? 'below' : 'above');
    // For 'above': stem rises to 80, bold title at 60, small sub at 35.
    // For 'below': stem rises to 200, bold title at 180, small sub at 155.
    const stemTopY = side === 'above' ? 80 : 200;
    const titleY = side === 'above' ? 60 : 180;
    const subY = side === 'above' ? 35 : 155;
    const future = !!m.future;
    const key = registerDetail(m.detail);
    const tlAttr = key ? ` data-tl="${key}"` : '';

    if (future) {
      // Ringed, larger future/now dot.
      parts.push(
        `<circle class="tl-dot future" cx="${cx}" cy="${axisY}" r="14"${tlAttr}/>`,
      );
      parts.push(`<circle class="tl-ring" cx="${cx}" cy="${axisY}" r="22"/>`);
    } else {
      const dotCls = 'tl-dot';
      parts.push(
        `<circle class="${dotCls}" cx="${cx}" cy="${axisY}" r="10"${tlAttr}/>`,
      );
    }
    parts.push(`<line class="tl-stem" x1="${cx}" y1="${axisY}" x2="${cx}" y2="${stemTopY}"/>`);
    parts.push(
      `<text class="tl-title" x="${cx}" y="${titleY}" text-anchor="middle">${escapeHtml(m.label)}</text>`,
    );
    if (m.when) {
      const subCls = future ? 'tl-sub future' : 'tl-sub';
      parts.push(
        `<text class="${subCls}" x="${cx}" y="${subY}" text-anchor="middle">${escapeHtml(m.when)}</text>`,
      );
    }
  });

  // Optional secondary "ongoing" rail beneath the axis.
  if (b.ongoing) {
    const eyebrow = b.ongoing.label ? b.ongoing.label : 'ONGOING';
    parts.push(`<text class="tl-rail-eyebrow" x="60" y="245">${escapeHtml(eyebrow)}</text>`);
    parts.push(`<line class="tl-rail" x1="190" y1="240" x2="1640" y2="240"/>`);
    (b.ongoing.items ?? []).forEach((item) => {
      const rx = Math.round(x0 + span * (Number.isFinite(item.at) ? item.at : 0));
      parts.push(
        `<text class="tl-rail-label" x="${rx}" y="220" text-anchor="middle">${escapeHtml(item.label)}</text>`,
      );
    });
  }

  return `<div class="timeline"><svg class="timeline-svg" viewBox="0 0 1700 360">${parts.join('')}</svg></div>`;
}

/** A feature/advantage card grid — icon + eyebrow + title + desc. A card with a
 *  `detail` becomes a clickable card (data-detail-key) that opens the modal; one
 *  optional navy accent card. Columns clamp to 2..4 (default 3). */
function renderFeatureGridBlock(
  b: Extract<Block, { type: 'feature-grid' }>,
): string {
  const cols = Math.max(2, Math.min(4, typeof b.columns === 'number' ? Math.floor(b.columns) : 3));
  const cards = b.items
    .map((item) => {
      const key = registerDetail(item.detail);
      const mods: string[] = [];
      if (item.accent) mods.push('accent');
      if (key) mods.push('clickable-card');
      const cls = mods.length ? `feature-card ${mods.join(' ')}` : 'feature-card';
      const dataAttr = key ? ` data-detail-key="${key}"` : '';
      const iconSvg = sanitiseIconSvg(item.icon);
      const icon = iconSvg ? `<span class="fc-icon">${iconSvg}</span>` : '';
      const eyebrow = item.eyebrow
        ? `<div class="fc-eyebrow">${escapeHtml(item.eyebrow)}</div>`
        : '';
      const title = `<h3 class="fc-title">${escapeHtml(item.title)}</h3>`;
      const desc = item.desc ? `<p class="fc-desc">${escapeHtml(item.desc)}</p>` : '';
      return `<div class="${cls}"${dataAttr}>${icon}${eyebrow}${title}${desc}</div>`;
    })
    .join('');
  return `<div class="feature-grid" style="--fg-cols:${cols}">${cards}</div>`;
}

/** A titled card that wraps a FLAT list of blocks. Optional `detail` makes the
 *  whole panel click-to-expand (data-detail-key + the + affordance). An optional
 *  navy accent treatment. */
function renderPanelBlock(b: Extract<Block, { type: 'panel' }>): string {
  const key = registerDetail(b.detail);
  const mods: string[] = [];
  if (b.accent) mods.push('accent');
  if (key) mods.push('clickable-card');
  const cls = mods.length ? `panel ${mods.join(' ')}` : 'panel';
  const dataAttr = key ? ` data-detail-key="${key}"` : '';
  const label = b.label ? `<div class="panel-label">${escapeHtml(b.label)}</div>` : '';
  const title = b.title ? `<h3 class="panel-title">${escapeHtml(b.title)}</h3>` : '';
  const inner = b.blocks.map((blk) => renderBlock(blk, undefined)).join('');
  return `<div class="${cls}"${dataAttr}>${label}${title}${inner}</div>`;
}

/** The one "smart layout" primitive — a generic two-column split. Each side is a
 *  flat list of blocks. `ratio` is allowlisted to a grid-template; `align`
 *  centres or tops the columns. */
const SPLIT_RATIOS: Record<string, string> = {
  '1-1': '1fr 1fr',
  '5-7': '5fr 7fr',
  '7-5': '7fr 5fr',
  '4-6': '4fr 6fr',
  '6-4': '6fr 4fr',
  '2-1': '2fr 1fr',
  '1-2': '1fr 2fr',
};

function renderSplitBlock(b: Extract<Block, { type: 'split' }>): string {
  const cols = (b.ratio && SPLIT_RATIOS[b.ratio]) || '1fr 1fr';
  const alignVal = b.align === 'center' ? 'center' : 'start';
  const left = b.left.map((blk) => renderBlock(blk, undefined)).join('');
  const right = b.right.map((blk) => renderBlock(blk, undefined)).join('');
  return (
    `<div class="split" style="--split-cols:${cols};--split-align:${alignVal}">` +
    `<div class="split-col">${left}</div>` +
    `<div class="split-col">${right}</div>` +
    `</div>`
  );
}

/** Render a single block to its HTML string. The `variant` lets the title block
 *  adapt to cover/divider layouts. */
function renderBlock(block: Block, variant: SlideVariant | undefined): string {
  switch (block.type) {
    case 'title':
      return renderTitleBlock(block, variant);
    case 'heading':
      return renderHeadingBlock(block);
    case 'paragraph':
      return `<p class="body">${escapeHtml(block.text)}</p>`;
    case 'lede':
      return `<p class="lede">${escapeWithBreaks(block.text)}</p>`;
    case 'bullets':
      return renderBulletsBlock(block);
    case 'cards':
      return renderCardsBlock(block);
    case 'stat-row':
      return renderStatRowBlock(block);
    case 'quote':
      return renderQuoteBlock(block);
    case 'table':
      return renderTableBlock(block);
    case 'kv':
      return renderKvBlock(block);
    case 'rule':
      return '<hr class="rule">';
    case 'raw-html':
      // AUDITED escape hatch — inserted verbatim, deliberately not escaped.
      return block.html;
    // ── Wave 3 ──
    case 'media-split':
      return renderMediaSplitBlock(block);
    case 'media-full':
      return renderMediaFullBlock(block);
    case 'media-grid':
      return renderMediaGridBlock(block);
    case 'figure':
      return renderFigureBlock(block);
    case 'bignum':
      return renderBignumBlock(block);
    case 'testimonial':
      return renderTestimonialBlock(block);
    case 'chart-bars':
      return renderChartBarsBlock(block);
    case 'donut':
      return renderDonutBlock(block);
    case 'chart-line':
      return renderChartLineBlock(block);
    case 'agenda':
      return renderAgendaBlock(block);
    case 'compare':
      return renderCompareBlock(block);
    case 'proscons':
      return renderProsConsBlock(block);
    case 'feature-card':
      return renderFeatureCardBlock(block);
    case 'icon-list':
      return renderIconListBlock(block);
    case 'cta':
      return renderCtaBlock(block);
    // ── Drift-closing ──
    case 'apps-hub':
      return renderAppsHubBlock(block);
    case 'tier-cap':
      return renderTierCapBlock(block);
    case 'region-grid':
      return renderRegionGridBlock(block);
    case 'signal-flow':
      return renderSignalFlowBlock(block);
    case 'floorplan':
      return renderFloorplanBlock(block);
    case 'layer-band':
      return renderLayerBandBlock(block);
    case 'ai-stack':
      return renderAiStackBlock(block);
    case 'engine-grid':
      return renderEngineGridBlock(block);
    case 'cycle':
      return renderCycleBlock(block);
    case 'roadmap':
      return renderRoadmapBlock(block);
    case 'bar-h':
      return renderBarHBlock(block);
    case 'pricing-tbl':
      return renderPricingTblBlock(block);
    // ── TIER-C ──
    case 'callout-banner':
      return renderCalloutBannerBlock(block);
    case 'equation':
      return renderEquationBlock(block);
    case 'chip-wall':
      return renderChipWallBlock(block);
    case 'command-hub':
      return renderCommandHubBlock(block);
    case 'before-after':
      return renderBeforeAfterBlock(block);
    // ── V5 ──
    case 'split':
      return renderSplitBlock(block);
    case 'panel':
      return renderPanelBlock(block);
    case 'timeline':
      return renderTimelineBlock(block);
    case 'feature-grid':
      return renderFeatureGridBlock(block);
    case 'progress':
      return renderProgressBlock(block);
    default: {
      // Exhaustiveness guard: if a new block type is added to the union without
      // a renderer, TypeScript flags this assignment at compile time.
      const _exhaustive: never = block;
      return _exhaustive;
    }
  }
}

function renderSlide(slide: Slide, index: number): string {
  // Set the render state used by registerDetail() so any detail key minted while
  // rendering this slide's blocks is namespaced s${index}-${seq}.
  _slideIndex = index;
  _detailSeq = 0;
  const cls = sectionClasses(slide);
  const classAttr = cls ? ` class="${cls}"` : '';
  const labelAttr = slide.label ? ` data-label="${escapeHtml(slide.label)}"` : '';
  // The background layer is emitted FIRST so it is the first section child;
  // deck-stage.js hoists absolutely-positioned children out of .deck-fit, so it
  // fills the section edge-to-edge and is never scaled by auto-fit.
  const bg = slide.background ? renderBackground(slide.background) : '';
  const body = slide.blocks.map((b) => renderBlock(b, slide.variant)).join('\n      ');
  // No per-slide footer chrome: the brand strip was removed to reclaim vertical
  // height (the deck-stage already shows slide position in its own chrome). The
  // `slide.bare` flag remains in the model for future chrome suppression.
  return `    <section${classAttr}${labelAttr}>\n      ${bg}${body}\n    </section>`;
}

/** Resolve the speaker-notes array: parallel `model.speakerNotes` takes
 *  precedence per index, falling back to each slide's `speakerNote`, padded to
 *  exactly one entry per slide. */
function resolveSpeakerNotes(model: DeckModel): string[] {
  return model.slides.map((slide, i) => {
    const fromArray = model.speakerNotes?.[i];
    if (typeof fromArray === 'string') return fromArray;
    if (typeof slide.speakerNote === 'string') return slide.speakerNote;
    return '';
  });
}

/** JSON-encode the notes for embedding in a <script type="application/json">.
 *  The `<` escape neutralises any literal `</script>` so a note can never break
 *  out of the block (JSON.parse still reads < as "<"). */
function encodeSpeakerNotesJSON(notes: string[]): string {
  return JSON.stringify(notes).replace(/</g, '\\u003c');
}

/**
 * Compile a DeckModel into a complete self-contained deck-system HTML document.
 *
 * @param model the authoritative DeckModel
 * @returns a full HTML document string ready to store as Deck.html
 */
export function renderDeck(model: DeckModel): string {
  // Reset the per-render detail-modal state so a second compile never inherits
  // the first deck's registry or key counters.
  _detailRegistry = {};
  _slideIndex = 0;
  _detailSeq = 0;

  const meta = model.meta;
  const title = escapeHtml(meta.title);

  // meta.theme: "light"/"dark" -> data-theme on <html>. "auto"/absent leaves
  // the theme-toggle.js + localStorage waterfall in charge (no attribute).
  const themeAttr =
    meta.theme === 'light' || meta.theme === 'dark' ? ` data-theme="${meta.theme}"` : '';

  // meta.accent (WAVE 3): when it is one of the allowed presets, emit a
  // data-accent attribute on <html>; the W3.1 token layer in deck-system.css
  // re-points --accent/-soft/-on accordingly. An unknown/hostile value is
  // dropped by sanitiseAccent, so it can never reach the attribute.
  const accent = sanitiseAccent(meta.accent);
  const accentAttr = accent ? ` data-accent="${accent}"` : '';

  // Deck-meta pin text: "Internal · DATE" when a date is supplied, else a bare
  // "Internal" badge. The leading red dot is a CSS ::before, no markup needed.
  const pinText = meta.date ? `Internal &middot; ${escapeHtml(meta.date)}` : 'Internal';

  const notes = resolveSpeakerNotes(model);
  const notesJSON = encodeSpeakerNotesJSON(notes);

  // Render every slide — this also populates _detailRegistry via registerDetail()
  // as the feature-grid / panel / timeline blocks render.
  const sections = model.slides
    .map((slide, i) => renderSlide(slide, i))
    .join('\n\n');

  // After all slides render, emit ONE script that registers the detail-modal map
  // (only when at least one detail was minted). Placed just before the
  // DeckOverlays.init() boot call so the registry is populated before init wires
  // the click handlers. scriptSafeJson() neutralises any </script> breakout; the
  // payload strings stay RAW because the overlay injects them via textContent.
  const detailScript = Object.keys(_detailRegistry).length
    ? `\n  <script>DeckOverlays.registerDetails(${scriptSafeJson(_detailRegistry)});</script>`
    : '';

  return `<!doctype html>
<!--
  Generated by @rdx/deck-system render-deck.ts from a DeckModel.
  Self-contained: boots the shared deck-system library via absolute /src/* paths
  (the deck-system service mounts /src, /components, /examples at the root).
  Do not hand-edit — re-render from the DeckModel instead.
-->
<html lang="en"${themeAttr}${accentAttr}>
<head>
  <meta charset="utf-8">
  <title>${title} — Workplace by Ricoh</title>
  <meta name="viewport" content="width=device-width, initial-scale=1">

  <!-- Theme boot — sets data-theme on <html> before first paint to prevent FOUC -->
  <script src="/src/theme-toggle.js"></script>
  <script src="/src/deck-bridge.js"></script>
  <script>DeckTheme.bootSync();</script>

  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500&display=swap">
  <link rel="stylesheet" href="/src/deck-system.css">
  <style>
    deck-stage:not(:defined) { visibility: hidden; }
  </style>

  <!-- Speaker notes — one entry per slide, in order. -->
  <script type="application/json" id="speaker-notes">
${notesJSON}
  </script>
</head>
<body>

  <!-- Persistent deck-meta pin — one element per deck, body-root, above the stage -->
  <div class="deck-meta-pin" aria-hidden="true">${pinText}</div>

  <deck-stage width="1920" height="1080">

${sections}

  </deck-stage>

  <!-- Host-side overlay markup (required by deck-overlays.js) -->
  <div class="fs-hint" id="fsHint">Press <kbd>F</kbd> fullscreen &middot; <kbd>&larr;</kbd> <kbd>&rarr;</kbd> navigate &middot; <kbd>?</kbd> show interactive &middot; <kbd>T</kbd> theme &middot; <kbd>S</kbd> settings &middot; <kbd>Esc</kbd> close</div>
  <div class="int-banner" id="intBanner"></div>
  <div class="gloss-tip" id="glossTip">
    <div class="term" id="glossTipTerm"></div>
    <div id="glossTipBody"></div>
  </div>
  <div class="detail-modal" id="detailModal" role="dialog" aria-modal="true" aria-hidden="true">
    <div class="panel">
      <button class="close" aria-label="Close" id="modalClose">&times;</button>
      <div class="label" id="modalLabel"></div>
      <h3 id="modalTitle"></h3>
      <div id="modalBody"></div>
      <div class="meta" id="modalMeta"></div>
    </div>
  </div>

  <!-- Boot order: stage → overlays → settings → init -->
  <script src="/src/deck-stage.js"></script>
  <script src="/src/deck-overlays.js"></script>
  <script src="/src/deck-settings.js"></script>${detailScript}
  <script>
    document.addEventListener('DOMContentLoaded', () => {
      DeckTheme.init();
      DeckOverlays.init();
      DeckSettings.init();
    });
  </script>

</body>
</html>
`;
}
